// The historical dock as a BAR CHART of COLUMNS: bars standing up from one baseline, side by side across the dock, the tallest at the left, each category's name under its bar. This is the chart in
// Chris's mock (2026-10-19: "Have also a bar chart option in the historical"; and when the first version drew horizontal rows instead: "The bars should go horizontal... DID YOU NOT LOOK AT MY MOCK
// UP"). Pure (no DOM, no d3): clusterService.ts feeds it the dock's categories and historicalDockDraw.ts draws what comes back.
import { barLength } from "./historicalBarList";
import { HistoricalRowsOrder, RowsBox, sortForRows } from "./historicalRows";

export interface ColumnItem {
  /** the category's name: unique among the dock's bubbles, and the tie-break so equal counts keep their order */
  id: string;
  value: number;
}

export interface ColumnMetrics {
  /** a column is never narrower than this (px between two bars' centres): past that many columns the smallest are left out */
  pitchMin: number;
  pitchMax: number;
  /** a bar is this share of its column's width, never wider than barMax and never thinner than barMin */
  barShare: number;
  barMin: number;
  barMax: number;
  margin: number;
  /** room above the tallest bar for the count written over it */
  valueZone: number;
  /** the shortest a bar with something in it is drawn */
  barFloor: number;
}

export const COLUMN_METRICS: ColumnMetrics = { pitchMin: 40, pitchMax: 120, barShare: 0.68, barMin: 14, barMax: 90, margin: 14, valueZone: 18, barFloor: 2 };

export interface ColumnSlot {
  id: string;
  index: number;
  /** the column's centre line */
  x: number;
  /** the width of the room its name has, and the width of its bar */
  room: number;
  barW: number;
  /** the bar's height (it stands on `baseline` and rises from it) */
  barH: number;
}

export interface ColumnsLayout {
  slots: Map<string, ColumnSlot>;
  /** the y of the line the bars stand on */
  baseline: number;
  /** the tallest a bar can be (the largest category's bar is this tall) */
  barAreaH: number;
  pitch: number;
  /** the block's left and right ends, for drawing the baseline under it */
  left: number;
  right: number;
  shown: number;
  /** categories too small to have a column (when there are more than the box can take) */
  hidden: number;
}

/**
 * Lays the sorted categories out as columns, left to right. `labelZone` is the room kept under the baseline for the icon and name. The block is centred across the box. When there are more categories than
 * the box can take at `pitchMin` apiece, the SMALLEST are left out and counted in `hidden` (the caller says so on the chart: a chart that silently dropped groups would be a wrong picture).
 */
export const layoutHistoricalColumns = (items: ColumnItem[], box: RowsBox, order: HistoricalRowsOrder, labelZone: number, m: ColumnMetrics = COLUMN_METRICS): ColumnsLayout => {
  const slots = new Map<string, ColumnSlot>();
  const W = Math.max(0, box.right - box.left - 2 * m.margin);
  const H = Math.max(0, box.bottom - box.top - 2 * m.margin);
  const barAreaH = Math.max(10, H - m.valueZone - labelZone);
  const baseline = box.top + m.margin + m.valueZone + barAreaH;
  if (items.length === 0) return { slots, baseline, barAreaH, pitch: 0, left: box.left, right: box.left, shown: 0, hidden: 0 };

  // the largest first: when some must go, it is the smallest that go, whichever way the chart is ordered
  const biggestFirst = sortForRows(items.map((i) => ({ id: i.id, value: i.value, size: 0 })), "desc");
  const capacity = Math.max(1, Math.floor(W / m.pitchMin));
  const kept = new Set(biggestFirst.slice(0, capacity).map((i) => i.id));
  const shownItems = items.filter((i) => kept.has(i.id));
  const sorted = sortForRows(shownItems.map((i) => ({ id: i.id, value: i.value, size: 0 })), order);
  const n = sorted.length;
  const pitch = Math.min(m.pitchMax, W / n);
  const barW = Math.max(m.barMin, Math.min(m.barMax, pitch * m.barShare));
  const blockW = pitch * n;
  const left = box.left + m.margin + Math.max(0, (W - blockW) / 2);
  const largest = Math.max(...shownItems.map((i) => i.value));
  const valueOf = new Map(shownItems.map((i) => [i.id, i.value]));
  sorted.forEach((item, index) => {
    slots.set(item.id, { id: item.id, index, x: left + pitch * index + pitch / 2, room: pitch, barW: Math.min(barW, pitch - 2), barH: barLength(valueOf.get(item.id) ?? 0, largest, barAreaH, m.barFloor) });
  });

  return { slots, baseline, barAreaH, pitch, left, right: left + blockW, shown: n, hidden: items.length - n };
};
